import React, { useEffect, useMemo, useState } from "react";
// ANT DESIGN COMPONENTS
import { Tag, Tooltip } from "antd";
// REDUX
import { useSelector } from "react-redux";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretRight } from "@fortawesome/free-solid-svg-icons";
// CUSTOM HOOKS
import { useLastLiftMessage, useDirectionForLift } from "../socketMessageState/BankMessagesContext";
// COMPONENTS
import IconDefinition from "../iconDefinition/IconDefinition";
import IconDefinitionEscalatorDefault from "../iconDefinition/IconDefinitionEscalatorDefault";
import MonitoringIconsMovingWalks from "../monitoringIcons/MonitoringIconsMovingWalks";
// INTERFACES
import { BankFloorInterface } from "../../../interfaces/EdgeManagement.interface";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { RootState } from "../../../app/store";
import { ServerInterface } from "../../../interfaces/Server.interface";
// CONSTANTS
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
// UTILS
import { getServiceModeDisplay } from "../../../utils/serviceModeDisplay";
import { bitmaskToDoorIconFiltered } from "../../../utils/bitmaskToIcon";
import { bitmaskPrefix } from "../../../utils/typedKeys";
import { stringToUuidT, EMPTY_UUID } from "../../../utils/uuid";

export default function ControlPanelLiftHeader({ server }: { server: ServerInterface }) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const { theme } = useSelector((state: RootState) => state.home);
  // Service mode display is handled via getServiceModeDisplay utility

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [currentPositionNameFloor, setCurrentPositionNameFloor] = useState<string>("");
  const [nextStopNameFloor, setNextStopNameFloor] = useState<string>("");
  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { liftSelected, bankSelected, doorUnknownAsOpening } = useSelector(
    (state: RootState) => state.monitoring,
  );

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const liftUuid = useMemo(
    () => (liftSelected.id ? stringToUuidT(liftSelected.id) : EMPTY_UUID),
    [liftSelected.id],
  );
  const socketMessageLift: SocketMessageInterface =
    useLastLiftMessage(liftUuid) || BLANK_SOCKET_MESSAGE;
  const hasFaults: boolean = socketMessageLift.faults;

  const prefix = useMemo(
    () => bitmaskPrefix(socketMessageLift.bankId, socketMessageLift.liftId),
    [socketMessageLift.bankId, socketMessageLift.liftId],
  );

  const hasFrontDoor = useMemo(
    () => liftSelected.lift_floors?.some((lf) => lf.front_door) ?? false,
    [liftSelected.lift_floors],
  );
  const hasRearDoor = useMemo(
    () => liftSelected.lift_floors?.some((lf) => lf.rear_door) ?? false,
    [liftSelected.lift_floors],
  );
  const liftHasMixedDoors = hasFrontDoor && hasRearDoor;

  const doorIcon = useMemo(() => {
    const icon = bitmaskToDoorIconFiltered(
      socketMessageLift.bitmask,
      prefix,
      hasFrontDoor,
      hasRearDoor,
      doorUnknownAsOpening,
      liftHasMixedDoors,
    );
    return icon;
  }, [
    socketMessageLift.bitmask,
    prefix,
    hasFrontDoor,
    hasRearDoor,
    doorUnknownAsOpening,
    liftHasMixedDoors,
  ]);
  const { directionIcon, lastDirectionIcon } = useDirectionForLift(liftUuid);

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getFloorName = (socketMessage_: SocketMessageInterface) => {
    bankSelected.bank_floors.map((floor: BankFloorInterface) => {
      if (socketMessage_.position === floor.floor_index) {
        setCurrentPositionNameFloor(floor.name);
      }
      if (
        socketMessage_.carCalls &&
        (socketMessage_.carCalls.front[0] || socketMessage_.carCalls.rear[0]) &&
        (socketMessage_.carCalls.front[0] === floor.floor_index ||
          socketMessage_.carCalls.rear[0] === floor.floor_index)
      ) {
        setNextStopNameFloor(floor.name);
      }
      return true;
    });
  };

  const getCarCallCircle = (
    carCall: number,
    type: "up" | "down" | "normal",
    keyValue: string,
  ) => {
    let floorName = `Error, floor ${carCall} not found or invalid configuration`;
    let floorError = true;
    bankSelected.bank_floors.map((floor: BankFloorInterface) => {
      if (carCall === floor.floor_index) {
        floorName = floor.name;
        floorError = false;
      }
      return true;
    });
    return (
      <Tooltip key={keyValue} title={floorName}>
        <div
          className={`monitoringView__headerCarCall ${
            floorError
              ? "monitoringView__headerCarCall__error"
              : `monitoringView__headerCarCall__${theme} ${
                  type === "up" ? "monitoringView__headerCarCall__up" : ""
                } ${type === "down" ? "monitoringView__headerCarCall__down" : ""}`
          }`}
        >
          {floorName.substring(0, 2).toUpperCase()}
        </div>
      </Tooltip>
    );
  };

  // ************************************************* */
  // USE EFFECT ************************************** */

  useEffect(() => {
    if (liftSelected.type.toUpperCase() === "ELEVATOR") {
      getFloorName(socketMessageLift);
    }
  }, [socketMessageLift]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div
      className={`monitoringView__controlPanel__${theme} ${
        hasFaults
          ? "monitoringView__dangerElevator monitoringView__dangerElevator__top monitoringView__dangerElevator__bottom"
          : ""
      }`}
    >
      <div
        className={`monitoringView__controlPanelDetails monitoringView__controlPanelDetails__${theme}`}
      >
        {/** ICON AND TAGS SERVICE / FAULTS */}
        <div className="generalStyles__flexCenter monitoring__100pxBox2">
          <div>
            <div style={{ textAlign: "center" }} className="mt-1">
              {liftSelected.type === "ELEVATOR" && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {doorIcon && (
                    <IconDefinition
                      isMap={false}
                      expanded={false}
                      liftType="ELEVATOR"
                      icon={doorIcon}
                      iconSize={50}
                    />
                  )}
                  {(directionIcon || lastDirectionIcon) && (
                    <span
                      style={{
                        opacity: !directionIcon && lastDirectionIcon ? 0.3 : 1,
                      }}
                    >
                      <IconDefinition
                        isMap={false}
                        expanded={false}
                        liftType="ELEVATOR"
                        icon={(directionIcon || lastDirectionIcon) as string}
                        iconSize={50}
                      />
                    </span>
                  )}
                </div>
              )}
              {liftSelected.type === "ESCALATOR" && (
                <IconDefinitionEscalatorDefault iconSize={40} mapViewClass="" lift={liftSelected} />
              )}
              {liftSelected.type === "MOVINGWALK" && (
                <MonitoringIconsMovingWalks
                  direction={socketMessageLift.direction}
                  iconSize={60}
                  mapViewClass=""
                />
              )}
            </div>
            <div className="generalStyles__flexCenter mb-3">
              {hasFaults && (
                <Tag
                  color="red"
                  style={{
                    width: "55px",
                    textAlign: "center",
                    marginTop: "10px",
                    marginLeft: "10px",
                  }}
                >
                  FAULT
                </Tag>
              )}
              {socketMessageLift.serviceMode &&
                (() => {
                  const display = getServiceModeDisplay(
                    socketMessageLift.serviceMode.messageType,
                    socketMessageLift.serviceMode.message?.message ?? null,
                  );
                  if (!display) return null;
                  return (
                    <Tooltip placement="top" title={display.displayName}>
                      <Tag
                        style={{
                          width: "55px",
                          textAlign: "center",
                          marginTop: "10px",
                        }}
                        color={display.color}
                      >
                        {display.abbreviation.substring(0, 6)}
                      </Tag>
                    </Tooltip>
                  );
                })()}
            </div>
          </div>
        </div>
        {/** CAR NAME, SOCKET MESSAGE AND CALLS */}
        <div
          className="generalStyles__mlFix25 generalStyles__width100 generalStyles__flex"
          style={{ alignItems: "center" }}
        >
          <div>
            <div style={{ marginTop: "-10px", height: "35px" }}>
              <span className="monitoring__modalSettingsHeader__liftName">{liftSelected.name}</span>
              <span className="generalStyles__mrFix generalStyles__mlFix">/</span>
              <span>{bankSelected.name}</span>
              <span className="generalStyles__mrFix generalStyles__mlFix">/</span>
              <span>{server.fb_enabled ? "FB" : "CMD"}</span>
            </div>
            {/** CAR CALLS */}
            <div
              className="generalStyles__flex"
              style={{ maxWidth: "315px", overflowX: "auto", padding: "10px" }}
            >
              <div className="generalStyles__flex">
                {socketMessageLift.carCalls.front.map((carCall: number, index: number) => (
                  getCarCallCircle(carCall, "normal", `car-front-${index}-${carCall}`)
                ))}
                {socketMessageLift.carCalls.rear.map((carCall: number, index: number) => (
                  getCarCallCircle(carCall, "normal", `car-rear-${index}-${carCall}`)
                ))}
                {socketMessageLift.hallCalls.upAssigned.front.map(
                  (carCall: number, index: number) => (
                    getCarCallCircle(carCall, "up", `up-front-${index}-${carCall}`)
                  ),
                )}
                {socketMessageLift.hallCalls.upAssigned.rear.map(
                  (carCall: number, index: number) => (
                    getCarCallCircle(carCall, "up", `up-rear-${index}-${carCall}`)
                  ),
                )}
                {socketMessageLift.hallCalls.downAssigned.front.map(
                  (carCall: number, index: number) => (
                    getCarCallCircle(carCall, "down", `down-front-${index}-${carCall}`)
                  ),
                )}
                {socketMessageLift.hallCalls.downAssigned.rear.map(
                  (carCall: number, index: number) => (
                    getCarCallCircle(carCall, "down", `down-rear-${index}-${carCall}`)
                  ),
                )}
              </div>
            </div>
          </div>
        </div>
        {/** ELEVATOR DIRECTION */}
        <div className="generalStyles__flex">
          <div style={{ alignItems: "center", marginTop: "-10px" }} className="generalStyles__flex">
            <div>{currentPositionNameFloor}</div>
            {(socketMessageLift.carCalls.front.length > 0 ||
              socketMessageLift.carCalls.rear.length > 0) && (
              <>
                <div>
                  <FontAwesomeIcon
                    icon={faCaretRight}
                    className="monitoring__modalSettingsHeader__icon"
                  />
                </div>
                <div>{nextStopNameFloor}</div>
              </>
            )}
          </div>
        </div>
      </div>{" "}
    </div>
  );
}
